import { urlControlPanel, urlCreateProduct, urlEditProduct } from '@/urls';
import { Link, LoaderFunctionArgs, useLoaderData, useRevalidator } from 'react-router';
import { getPagination, productApi } from '@repo/api-client';
import { Product } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { useCurrentClub } from '@/hooks/use-current-club';
import { CopyIcon, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useAsync } from '@repo/ui/hooks/use-async';
import { Image } from '@repo/ui/components/image';
import { NotOkStatusIndicator, OkStatusIndicator } from '@repo/ui/components/status-indicator';
import 'react-tooltip/dist/react-tooltip.css';
import { Outlet, useNavigate } from 'react-router';
import { route as createProductRoute } from './create-product';
import { DataTable } from '@repo/ui/components/data-table';
import { toast } from 'sonner';
import { InputTextWithClear } from '@repo/ui/components/input-text-with-clear';
import { useDebouncedQueryParam } from '@/hooks/use-debounced-query-param';

function ProductList() {
  const navigate = useNavigate();
  const { products } = useLoaderData<typeof route.loader>();
  const { value: search, setValue: setSearch } = useDebouncedQueryParam('name');
  const revalidator = useRevalidator();
  const club = useCurrentClub();
  const { execute: deleteProduct } = useAsync(async (productId: string) => {
    await productApi.deleteProduct(club.id, productId);
    toast.success('Producto eliminado correctamente');
    revalidator.revalidate();
  });

  const columns = [
    {
      header: 'Imagen',
      accessorKey: 'imageUrl',
      cell: ({ row }: { row: { original: Product } }) => {
        return (
          <Image
            src={row.original.imageUrl}
            alt={row.original.name}
            className="w-10 h-10 border border-gray-300 rounded-full"
          />
        );
      },
    },
    {
      header: 'Nombre',
      accessorKey: 'name',
    },
    {
      header: 'Precio',
      accessorKey: 'price',
      cell: ({ row }: { row: { original: Product } }) => {
        return `$${row.original.price}`;
      },
    },
    {
      header: 'Mostrar en POS',
      accessorKey: 'showOnPOS',
      cell: ({ row }: { row: { original: Product } }) => {
        return row.original.showOnPOS ? <OkStatusIndicator /> : <NotOkStatusIndicator />;
      },
    },
    {
      header: 'Categoría',
      accessorKey: 'category.name',
      cell: ({ row }: { row: { original: Product } }) => {
        return row.original.category?.name ?? '--';
      },
    },
    {
      header: 'Acciones',
      accessorKey: 'actions',
      cell: ({ row }: { row: { original: Product } }) => {
        const hasSales = (row.original.sales?.length ?? 0) > 0;
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button onClick={() => navigator.clipboard.writeText(row.original.sku)}>
              <CopyIcon className="w-4 h-4" />
              SKU
            </Button>
            <Button asChild>
              <Link to={urlEditProduct(club.id, row.original.id)} viewTransition>
                <PencilIcon className="w-4 h-4" />
                Editar
              </Link>
            </Button>
            <Button
              onClick={() => deleteProduct(row.original.id)}
              variant="destructive"
              disabled={hasSales}
            >
              <TrashIcon className="w-4 h-4" />
              Eliminar
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        total={products.count}
        onRowClick={(row) => navigate(urlEditProduct(club.id, row.id), { viewTransition: true })}
        columns={columns}
        data={products.results}
        mainActions={() => (
          <Button asChild>
            <Link to={urlCreateProduct(club.id)} viewTransition>
              <PlusIcon className="w-4 h-4" />
              Crear Producto
            </Link>
          </Button>
        )}
        filters={() => (
          <InputTextWithClear
            value={search}
            onChange={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            placeholder="Buscar por nombre..."
          />
        )}
      />
      <Outlet />
    </>
  );
}

export const route = {
  id: 'product-list',
  path: '',
  element: <ProductList />,
  loader: async ({ params, request }: LoaderFunctionArgs) => {
    const clubId = params.id as string;
    const pagination = getPagination(request);
    try {
      const products = await productApi.getAll(clubId, pagination);
      return { clubId, products };
    } catch {
      return { clubId, products: { results: [], count: 0 } };
    }
  },
  handle: {
    title: 'Productos',
    backLink: (params: { id: string }) => urlControlPanel(params.id),
  },
  children: [createProductRoute],
};
